import { createContext, useContext, useState, useEffect } from 'react';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from './AuthContext';

type ProfileSettings = {
  username: string;
  description: string;
  profilePicture: string;
  country: string;
  city: string;
  socialLinks: Array<{ platform: string; url: string }>;
  mobileMoneyAccounts: Array<{ type: string; number: string }>;
  externalPhotos: Array<{
    id: string;
    url: string;
    title?: string;
    description?: string;
  }>;
  showExternalPhotos: boolean;
  likes: number;
  referralCode: string;
};

const defaultSettings: ProfileSettings = {
  username: '',
  description: '',
  profilePicture: '',
  country: '',
  city: '',
  socialLinks: [],
  mobileMoneyAccounts: [],
  externalPhotos: [],
  showExternalPhotos: true,
  likes: 0,
  referralCode: ''
};

const ProfileSettingsContext = createContext<{
  profileSettings: ProfileSettings;
  setProfileSettings: (settings: ProfileSettings) => void;
}>({
  profileSettings: defaultSettings,
  setProfileSettings: () => {}
});

export function ProfileSettingsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [profileSettings, setProfileSettings] = useState<ProfileSettings>(defaultSettings);

  useEffect(() => {
    if (!user?.id) return;

    // Charger les données initiales
    const loadInitialData = async () => {
      try {
        // Récupérer les données de profileSettings
        const profileSettingsDoc = await getDoc(doc(db, 'profileSettings', user.id));
        const profileData = profileSettingsDoc.exists() ? profileSettingsDoc.data() : {};

        // Récupérer les données de users
        const userDoc = await getDoc(doc(db, 'users', user.id));
        const userData = userDoc.exists() ? userDoc.data() : {};

        // Récupérer la photo de profil du localStorage
        const savedProfilePicture = localStorage.getItem(`user_${user.id}_profilePicture`);

        // Fusionner toutes les données
        const mergedSettings = {
          ...defaultSettings,
          ...profileData,
          ...userData,
          profilePicture: savedProfilePicture || userData.profilePicture || '',
        };

        setProfileSettings(mergedSettings);
        
        // Sauvegarder dans le localStorage
        localStorage.setItem(`user_${user.id}_settings`, JSON.stringify(mergedSettings));
      } catch (error) {
        console.error('Erreur lors du chargement des paramètres:', error);
      }
    };

    // Écouter les changements dans profileSettings
    const unsubscribeProfileSettings = onSnapshot(doc(db, 'profileSettings', user.id), (doc) => {
      if (doc.exists()) {
        const newData = doc.data();
        setProfileSettings(prev => {
          const updated = { ...prev, ...newData };
          localStorage.setItem(`user_${user.id}_settings`, JSON.stringify(updated));
          return updated;
        });
      }
    });

    // Écouter les changements dans users
    const unsubscribeUser = onSnapshot(doc(db, 'users', user.id), (doc) => {
      if (doc.exists()) {
        const userData = doc.data();
        setProfileSettings(prev => {
          const updated = { ...prev, ...userData };
          localStorage.setItem(`user_${user.id}_settings`, JSON.stringify(updated));
          return updated;
        });
      }
    });

    loadInitialData();

    return () => {
      unsubscribeProfileSettings();
      unsubscribeUser();
    };
  }, [user?.id]);

  return (
    <ProfileSettingsContext.Provider value={{ profileSettings, setProfileSettings }}>
      {children}
    </ProfileSettingsContext.Provider>
  );
}

export const useProfileSettings = () => {
  const context = useContext(ProfileSettingsContext);
  if (!context) {
    throw new Error('useProfileSettings must be used within ProfileSettingsProvider');
  }
  return context;
}; 